const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const CATEGORY_KEYS = ['budget', 'forecast', 'scheduled', 'actual'];

export const DEFAULT_CATEGORY_VISIBILITY = {
  budget: true,
  forecast: true,
  scheduled: true,
  actual: false,
};

export const CATEGORY_PREF_STORAGE_KEY = 'workforce.weeklyLaborDashboard.categoryVisibility';

export const loadCategoryVisibility = () => {
  try {
    const raw = localStorage.getItem(CATEGORY_PREF_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CATEGORY_VISIBILITY };
    const parsed = JSON.parse(raw);
    return {
      budget: parsed.budget !== false,
      forecast: parsed.forecast !== false,
      scheduled: parsed.scheduled !== false,
      actual: parsed.actual === true,
    };
  } catch {
    return { ...DEFAULT_CATEGORY_VISIBILITY };
  }
};

export const saveCategoryVisibility = (visibility) => {
  try {
    localStorage.setItem(CATEGORY_PREF_STORAGE_KEY, JSON.stringify(visibility));
  } catch {
    // ignore quota / private mode
  }
};

export const sumDays = (days, key) =>
  (Array.isArray(days) ? days : []).reduce((acc, day) => acc + num(day?.[key]), 0);

export const avgDays = (days, key) => {
  const list = Array.isArray(days) ? days : [];
  if (!list.length) return 0;
  return sumDays(list, key) / list.length;
};

export const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

export const formatCurrencyFull = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const formatHours = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

export const formatPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  return `${n.toFixed(1)}%`;
};

export const laborPercent = (laborCost, revenue) => {
  const labor = num(laborCost);
  const rev = num(revenue);
  if (rev <= 0) return 0;
  return (labor / rev) * 100;
};

/** Estimate revenue from budget revenue scaled by labor ratio (POC when revenue actuals are unavailable). */
export const estimateRevenueFromLaborRatio = (budgetRevenue, numeratorLabor, denominatorLabor) => {
  const budget = num(budgetRevenue);
  const numerator = num(numeratorLabor);
  const denominator = num(denominatorLabor);
  if (budget <= 0) return 0;
  if (denominator <= 0) return budget;
  return budget * (numerator / denominator);
};

export const deriveDayRevenue = (day) => {
  const budgetRevenue = num(day?.budgetedAmount);
  const budgetLabor = num(day?.budgetedLaborCost);
  const forecastLabor = num(day?.forecastedLaborCost);
  const scheduledLabor = num(day?.scheduledLaborCost);
  const actualLabor = num(day?.actualLaborCost);
  const sageForecastRevenue = num(day?.forecastedAmount);

  return {
    budgetRevenue,
    // Prefer Sage GL_FORECAST_VIEW revenue when present; else estimate from labor ratio.
    forecastRevenue: sageForecastRevenue > 0
      ? sageForecastRevenue
      : estimateRevenueFromLaborRatio(budgetRevenue, forecastLabor, budgetLabor),
    scheduledRevenue: estimateRevenueFromLaborRatio(budgetRevenue, scheduledLabor, budgetLabor),
    actualRevenue: actualLabor > 0
      ? estimateRevenueFromLaborRatio(budgetRevenue, actualLabor, budgetLabor)
      : 0,
  };
};

export const buildDepartmentRows = (roles, budgets, weekDays, totals, departmentLabor = []) => {
  const roleById = new Map(
    (Array.isArray(roles) ? roles : []).map((role) => [role.rolePositionId || role.role_position_id, role])
  );

  const deptMap = new Map();

  const ensureDept = (name) => {
    const key = name || 'Unassigned';
    if (!deptMap.has(key)) {
      deptMap.set(key, {
        department: key,
        budgetLabor: 0,
        budgetHours: 0,
        scheduledLabor: 0,
        scheduledHours: 0,
        actualLabor: 0,
        actualHours: 0,
      });
    }
    return deptMap.get(key);
  };

  (Array.isArray(roles) ? roles : []).forEach((role) => {
    if (role?.department) ensureDept(role.department);
  });

  (Array.isArray(departmentLabor) ? departmentLabor : []).forEach((item) => {
    const row = ensureDept(item.department || 'Unassigned');
    row.scheduledLabor += num(item.scheduledLaborCost ?? item.scheduled_labor_cost);
    row.scheduledHours += num(item.scheduledLaborHours ?? item.scheduled_labor_hours);
    row.actualLabor += num(item.actualLaborCost ?? item.actual_labor_cost);
    row.actualHours += num(item.actualLaborHours ?? item.actual_labor_hours);
    // Sage Snowflake budget (preferred when present on week-summary departmentLabor)
    row.budgetLabor += num(item.budgetedLaborCost ?? item.budgeted_labor_cost);
    row.budgetHours += num(item.budgetedHours ?? item.budgeted_hours);
  });

  // Legacy Postgres workforce-budget rows (kept for compatibility; prefer Sage via departmentLabor above)
  (Array.isArray(budgets) ? budgets : []).forEach((budget) => {
    const roleId = budget.rolePositionId || budget.role_position_id;
    const role = roleId ? roleById.get(roleId) : null;
    const deptName = role?.department || budget.budgetName || budget.budget_name || 'Property';
    const row = ensureDept(deptName);
    // Skip if departmentLabor already carried Sage budget for this dept (avoid double count)
    const hasSageBudget = (Array.isArray(departmentLabor) ? departmentLabor : []).some((item) => {
      const name = (item.department || 'Unassigned').toLowerCase();
      return name === String(deptName).toLowerCase()
        && (num(item.budgetedLaborCost ?? item.budgeted_labor_cost) > 0
          || num(item.budgetedHours ?? item.budgeted_hours) > 0);
    });
    if (hasSageBudget) return;
    row.budgetLabor += num(budget.budgetedLaborCost ?? budget.budgeted_labor_cost);
    row.budgetHours += num(budget.budgetedHours ?? budget.budgeted_hours);
  });

  if (deptMap.size === 0) {
    ['Admin & General', 'Engineering', 'Food & Beverage', 'Front Office', 'Housekeeping'].forEach((dept) => {
      ensureDept(dept);
    });
  }

  const propertyBudgetLabor = (Array.isArray(budgets) ? budgets : [])
    .filter((budget) => !(budget.rolePositionId || budget.role_position_id))
    .reduce((acc, budget) => acc + num(budget.budgetedLaborCost ?? budget.budgeted_labor_cost), 0);
  const propertyBudgetHours = (Array.isArray(budgets) ? budgets : [])
    .filter((budget) => !(budget.rolePositionId || budget.role_position_id))
    .reduce((acc, budget) => acc + num(budget.budgetedHours ?? budget.budgeted_hours), 0);

  const sageDeptBudgetPresent = (Array.isArray(departmentLabor) ? departmentLabor : []).some(
    (item) => num(item.budgetedLaborCost ?? item.budgeted_labor_cost) > 0
      || num(item.budgetedHours ?? item.budgeted_hours) > 0
  );

  if (!sageDeptBudgetPresent && (propertyBudgetLabor > 0 || propertyBudgetHours > 0)) {
    const deptCount = deptMap.size || 1;
    deptMap.forEach((row) => {
      row.budgetLabor += propertyBudgetLabor / deptCount;
      row.budgetHours += propertyBudgetHours / deptCount;
    });
  }

  const totalBudgetLabor = [...deptMap.values()].reduce((acc, row) => acc + row.budgetLabor, 0);
  const totalBudgetHours = [...deptMap.values()].reduce((acc, row) => acc + row.budgetHours, 0);

  const {
    forecastLabor: weekForecastLabor,
    forecastHours: weekForecastHours,
  } = totals;

  const occupiedRooms = sumDays(weekDays, 'occupiedRooms');

  return [...deptMap.values()]
    .map((row) => {
      const share = totalBudgetLabor > 0 ? row.budgetLabor / totalBudgetLabor : 1 / deptMap.size;
      const hoursShare = totalBudgetHours > 0 ? row.budgetHours / totalBudgetHours : share;
      const forecastLabor = weekForecastLabor * share;
      const forecastHours = weekForecastHours * hoursShare;
      const variance = row.scheduledLabor - forecastLabor;
      const variancePct = forecastLabor > 0 ? (variance / forecastLabor) * 100 : 0;

      let status = 'Spot On';
      if (variancePct > 5) status = 'Stress';
      else if (variancePct < -5) status = 'Under';

      const mpor = occupiedRooms > 0 ? (row.scheduledHours * 60) / occupiedRooms : 0;

      return {
        ...row,
        forecastLabor,
        forecastHours,
        variance,
        mpor,
        status,
      };
    })
    .sort((a, b) => b.budgetLabor - a.budgetLabor || b.scheduledLabor - a.scheduledLabor);
};

export const buildWeekTotals = (days) => {
  const budgetLabor = sumDays(days, 'budgetedLaborCost');
  const forecastLabor = sumDays(days, 'forecastedLaborCost');
  const scheduledLabor = sumDays(days, 'scheduledLaborCost');
  const actualLabor = sumDays(days, 'actualLaborCost');
  const budgetHours = sumDays(days, 'budgetedHours');
  const forecastHours = sumDays(days, 'forecastedHours');
  const scheduledHours = sumDays(days, 'scheduledLaborHours');
  const actualHours = sumDays(days, 'actualLaborHours');

  const budgetRevenue = sumDays(days, 'budgetedAmount');
  const forecastRevenue = days.reduce((acc, day) => acc + deriveDayRevenue(day).forecastRevenue, 0);
  const scheduledRevenue = days.reduce((acc, day) => acc + deriveDayRevenue(day).scheduledRevenue, 0);
  const actualRevenue = days.reduce((acc, day) => acc + deriveDayRevenue(day).actualRevenue, 0);

  return {
    budgetLabor,
    forecastLabor,
    scheduledLabor,
    actualLabor,
    budgetHours,
    forecastHours,
    scheduledHours,
    actualHours,
    budgetRevenue,
    forecastRevenue,
    scheduledRevenue,
    actualRevenue,
    budgetLaborPct: laborPercent(budgetLabor, budgetRevenue),
    forecastLaborPct: laborPercent(forecastLabor, forecastRevenue),
    scheduledLaborPct: laborPercent(scheduledLabor, scheduledRevenue),
    actualLaborPct: laborPercent(actualLabor, actualRevenue),
  };
};
